import { apiRequest } from '@/shared/api/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import type { Match, MatchStatus } from '../model/types';

type ApiStatus = 'PENDING' | 'ACCEPTED' | 'SKIPPED';

type MatchOtherUserDto = {
  id: string;
  nome: string | null;
  avatarUrl: string | null;
  cargo: string | null;
  empresa: string | null;
  segmento: string | null;
  networkingLevel: number;
  networkingColor: string;
  tagsBusca: string[];
  tagsOferta: string[];
};

type MatchResponseDto = {
  matchId: string;
  eventoId: string;
  score: number;
  altoPotencial: boolean;
  motivo: string | null;
  scoreBreakdown: Record<string, number>;
  statusMine: ApiStatus;
  statusOther: ApiStatus;
  otherUser?: MatchOtherUserDto;
};

type MatchListDto = {
  items: MatchResponseDto[];
  total: number;
  page: number;
  limit: number;
};

type RespondMatchDto = {
  match: MatchResponseDto;
  becameMutual: boolean;
};

const toLocalStatus = (mine: ApiStatus, other: ApiStatus): MatchStatus => {
  if (mine === 'SKIPPED') return 'PULADO';
  if (mine === 'ACCEPTED' && other === 'ACCEPTED') return 'CONVERSANDO';
  if (mine === 'ACCEPTED') return 'ACEITO';
  return 'SUGERIDO';
};

const tagsToObjects = (tags: string[]): { id: string; nome: string }[] =>
  tags.map((nome) => ({ id: nome, nome }));

export const fromMatchDto = (dto: MatchResponseDto): Match => {
  const other = dto.otherUser;
  return {
    id: dto.matchId,
    status: toLocalStatus(dto.statusMine, dto.statusOther),
    score: dto.score,
    altoPotencial: dto.altoPotencial,
    motivos: dto.motivo ? [dto.motivo] : [],
    participante: {
      id: other?.id ?? '',
      nome: other?.nome ?? 'Participante',
      fotoUrl: other?.avatarUrl ?? null,
      cargo: other?.cargo ?? null,
      empresa: other?.empresa ?? null,
      tipoPerfil: 'EMPRESARIO',
      bio: null,
      tagsBusca: tagsToObjects(other?.tagsBusca ?? []),
      tagsOferta: tagsToObjects(other?.tagsOferta ?? []),
    },
    createdAt: new Date().toISOString(),
  };
};

export const matchApi = {
  list: async (eventoId: string): Promise<{ matches: Match[] }> => {
    const out = await apiRequest<MatchListDto>({
      method: 'GET',
      url: ENDPOINTS.MATCHES.LIST_ME(eventoId),
    });
    return { matches: out.items.map(fromMatchDto) };
  },
  respond: async (
    matchId: string,
    status: 'ACCEPTED' | 'SKIPPED',
  ): Promise<{ match: Match; becameMutual: boolean }> => {
    const out = await apiRequest<RespondMatchDto>({
      method: 'PATCH',
      url: ENDPOINTS.MATCHES.RESPOND(matchId),
      data: { status },
    });
    return { match: fromMatchDto(out.match), becameMutual: out.becameMutual };
  },
  accept: (matchId: string) => matchApi.respond(matchId, 'ACCEPTED'),
  skip: (matchId: string) => matchApi.respond(matchId, 'SKIPPED'),
};
